/**
 * screenshotCapture — 使用者附上的截圖:縮圖、編碼、把標註框燒進圖片
 * =============================================================================
 *
 * ## 🔴 R257:**自動截圖(DOM 重繪)整條移除**
 *
 * 原本用 `html-to-image` 把整棵 DOM 連同每個節點的 computed style 重繪成 SVG,再解碼成
 * 點陣圖。那條路在實測裡連續踩了三個坑,而**最後一個是原理性的、修不掉**:
 *
 * 1. 跨域 stylesheet 會讓它掛住(R250 補 `skipFonts` + 逾時)
 * 2. library 的 `createImage` 卡在 `requestAnimationFrame` / `decode` 沒有 catch(R256 自己重寫)
 * 3. 🔴 **節點越多越慢** —— 畫布(reactflow)與成效報表(數百列)實測都拍不出來。
 *    這不是 bug 而是原理:它必須序列化每個節點的完整 computed style,連登入頁那種簡單畫面
 *    都已經產出 **562 KB** 的 SVG。
 *
 * Bryan 2026-08-19 拍板:「不如乖乖螢幕截圖然後直接圈起來就好…不然這樣截圖超過 1-2 秒
 * 根本沒人願意。」
 *
 * **系統截圖(Cmd+Shift+4 → 貼上)零等待、零依賴,而且品質更好** —— 它拍的是真實畫面,
 * 跨域素材縮圖拍得到,而 DOM 重繪那條**永遠**拍不到(canvas 會被 taint)。
 *
 * ⚠️ 連帶移除「圈選反查元件」(`elementsFromPoint` → `data-testid`):它只對「我們自己拍的
 * 那一屏」成立 —— 系統截圖與當下 DOM 沒有已知座標關係,硬反查會給出**看起來合理但錯的**
 * testid(判準見 R250)。自動截圖沒了,它自然失去用武之地。
 *
 * ## ✅ R257.1:改用**原生螢幕擷取**(`getDisplayMedia`)自動帶圖
 *
 * 「使用者自己截圖再貼上」實測下來不方便(Bryan:「沒辦法系統帶圖 這樣不方便用戶」)。
 * 原生擷取沒有 DOM 重繪那條路的原理問題:
 *
 * | | DOM 重繪(已移除) | 原生擷取 |
 * |---|---|---|
 * | 原理 | 遍歷每個節點 + 序列化 computed style | 直接取 GPU 已合成好的畫面 |
 * | 速度 | **與畫面複雜度成正比**(畫布拍不出來) | **與複雜度無關**,毫秒級 |
 * | 跨域素材縮圖 | 永遠拍不到(canvas taint) | 拍得到(拍的是螢幕像素) |
 *
 * ⚠️ 代價是**瀏覽器強制的一次確認對話框**(安全要求,任何 API 都跳不掉)。
 * `preferCurrentTab` 讓 Chrome 預選當前分頁,實務上就是多按一下。
 *
 * 於是這支有四件事:**原生擷取、縮到合理大小、編碼、把標註框燒進去**。
 */

/** 截圖長邊上限。1280 足以看清版面與文字,又讓 base64 後的體積落在可接受範圍。 */
export const SHOT_MAX_EDGE = 1280;
/** WebP 品質。截圖是 UI 不是照片,0.8 下文字仍清楚。 */
const SHOT_QUALITY = 0.8;
/** 單張上限;超過就再降一級品質重編一次(見 {@link encodeShot})。 */
export const SHOT_MAX_BYTES = 400 * 1024;

/** 一張準備好要送出的截圖。 */
export interface CapturedShot {
  /** `data:image/webp;base64,…` — 可直接餵 `<img src>`。 */
  dataUrl: string;
  width: number;
  height: number;
  bytes: number;
}

/** 送出用的形狀(mime 與資料分開兩欄,BE 自己組回來)。 */
export interface ShotForUpload {
  mime: string;
  dataBase64: string;
  width: number;
  height: number;
  bytes: number;
}

function bytesOfDataUrl(dataUrl: string): number {
  const comma = dataUrl.indexOf(',');
  if (comma < 0) return 0;
  const b64 = dataUrl.slice(comma + 1);
  const padding = b64.endsWith('==') ? 2 : b64.endsWith('=') ? 1 : 0;
  return Math.floor((b64.length * 3) / 4) - padding;
}

/**
 * 縮到長邊 {@link SHOT_MAX_EDGE} 以內並編成 WebP;超過 {@link SHOT_MAX_BYTES} 再降一級品質。
 *
 * ⚠️ **不用「瀏覽器宣稱支援什麼」當判準,用結果大小**:`toDataURL('image/webp')` 在不支援
 * WebP 時會**靜默**退回 PNG(R245 封面踩過同一條),而 UI 截圖轉 PNG 有時反而更小 ——
 * 直接量結果就好,不必偵測。
 */
async function encodeShot(source: HTMLCanvasElement): Promise<CapturedShot> {
  const longest = Math.max(source.width, source.height);
  let canvas = source;
  if (longest > SHOT_MAX_EDGE) {
    const scale = SHOT_MAX_EDGE / longest;
    const scaled = document.createElement('canvas');
    scaled.width = Math.round(source.width * scale);
    scaled.height = Math.round(source.height * scale);
    const ctx = scaled.getContext('2d');
    if (ctx) {
      ctx.drawImage(source, 0, 0, scaled.width, scaled.height);
      canvas = scaled;
    }
  }
  let dataUrl = canvas.toDataURL('image/webp', SHOT_QUALITY);
  let bytes = bytesOfDataUrl(dataUrl);
  if (bytes > SHOT_MAX_BYTES) {
    dataUrl = canvas.toDataURL('image/webp', 0.6);
    bytes = bytesOfDataUrl(dataUrl);
  }
  return { dataUrl, width: canvas.width, height: canvas.height, bytes };
}

/**
 * 使用者貼上 / 選檔的圖 → 縮圖 + 編碼。
 *
 * 失敗一律回 `null` 不 throw —— 附圖是加分項,壞掉不該讓整筆回報送不出去。
 */
export async function prepareShotFromFile(file: File): Promise<CapturedShot | null> {
  if (!file.type.startsWith('image/')) return null;
  try {
    // ⚠️ `imageOrientation: 'from-image'` 必帶:canvas 重編碼會丟掉 EXIF,手機直拍的
    // 直式截圖會被轉倒(R245 封面踩過,那邊有測試釘死同一個參數)。
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(bitmap, 0, 0);
    bitmap.close();
    return await encodeShot(canvas);
  } catch {
    return null;
  }
}

/** 等第一幀真的有內容。`play()` resolve **不保證**已經有畫面可畫。 */
function waitForFrame(video: HTMLVideoElement, timeoutMs = 3000): Promise<boolean> {
  // HAVE_CURRENT_DATA 以上代表當前幀可用。
  if (video.readyState >= 2) return Promise.resolve(true);
  return new Promise((resolve) => {
    const timer = window.setTimeout(() => resolve(false), timeoutMs);
    video.onloadeddata = () => {
      window.clearTimeout(timer);
      resolve(true);
    };
  });
  // ⚠️ 刻意**不用 `requestAnimationFrame`** 等幀:它在背景 / 隱藏分頁不觸發,
  // 那正是 R256 那隻 hang 的成因。`onloadeddata` 沒有這個問題。
}

/**
 * 使用者取消授權 —— 那不是失敗,是他改變主意了,不該顯示錯誤。
 */
function isUserCancelled(e: unknown): boolean {
  return e instanceof DOMException && (e.name === 'NotAllowedError' || e.name === 'AbortError');
}

export type ScreenCaptureOutcome =
  | { kind: 'ok'; shot: CapturedShot }
  | { kind: 'cancelled' }
  | { kind: 'unsupported' }
  | { kind: 'failed' };

/**
 * 原生螢幕擷取 → 一張截圖。
 *
 * 流程:`getDisplayMedia`(瀏覽器彈確認)→ 把串流餵給一個隱藏的 `<video>` → 取一幀畫進
 * canvas → **立刻停掉串流**(不停的話分頁會一直顯示「正在分享畫面」)。
 *
 * ⚠️ 必須在**使用者手勢**內呼叫(點擊後幾秒內),否則 Chrome 直接拒絕。呼叫端不要在中間
 * 插入 `await`。
 *
 * 回傳用 discriminated union 而不是 `null`:**取消**與**失敗**要分開 —— 使用者按了「不分享」
 * 卻看到一句紅字錯誤,是很差的體驗。
 */
export async function captureScreen(): Promise<ScreenCaptureOutcome> {
  const media = navigator.mediaDevices;
  if (!media?.getDisplayMedia) return { kind: 'unsupported' };

  let stream: MediaStream | null = null;
  try {
    stream = await media.getDisplayMedia({
      // `preferCurrentTab` / `selfBrowserSurface` 不在標準 lib 型別裡(Chrome 擴充),
      // 但它們正是讓「多按一下」成立的關鍵:預選當前分頁、且允許選自己。
      video: { displaySurface: 'browser' },
      audio: false,
      preferCurrentTab: true,
      selfBrowserSurface: 'include',
      surfaceSwitching: 'exclude',
    } as DisplayMediaStreamOptions);

    const video = document.createElement('video');
    video.srcObject = stream;
    video.muted = true;
    video.playsInline = true;
    await video.play().catch(() => undefined);
    if (!(await waitForFrame(video))) return { kind: 'failed' };

    const width = video.videoWidth;
    const height = video.videoHeight;
    if (width <= 0 || height <= 0) return { kind: 'failed' };

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return { kind: 'failed' };
    ctx.drawImage(video, 0, 0, width, height);
    video.srcObject = null;

    return { kind: 'ok', shot: await encodeShot(canvas) };
  } catch (e) {
    return isUserCancelled(e) ? { kind: 'cancelled' } : { kind: 'failed' };
  } finally {
    // 一定要停 —— 否則分頁會持續顯示「正在分享畫面」,而我們只需要一幀。
    for (const track of stream?.getTracks() ?? []) track.stop();
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('screenshot decode failed'));
    img.src = src;
  });
}

/**
 * 把標註框**燒進**截圖,轉成送出用的形狀。
 *
 * 為什麼燒進去而不是只送座標:後端與 AI 打開圖片就直接看到紅框,不必自己拿座標去疊。
 * 座標仍然照送(給程式用)。
 *
 * 框的顏色**刻意寫死** `#E06666`(= `--err` 的值):這是要畫進 canvas 的像素,
 * `getContext('2d')` 不解析 CSS 變數 —— 拿 `var(--err)` 進去會是無效值,框整個不見。
 */
export async function renderAnnotatedShot(
  shot: CapturedShot,
  annotations: readonly { rect: readonly [number, number, number, number] }[],
): Promise<ShotForUpload | null> {
  try {
    const img = await loadImage(shot.dataUrl);
    const canvas = document.createElement('canvas');
    canvas.width = shot.width;
    canvas.height = shot.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0);

    if (annotations.length > 0) {
      // eslint-disable-next-line hai/no-hardcoded-colors -- canvas 2d 不解析 CSS var,見上方 JSDoc
      ctx.strokeStyle = '#E06666';
      ctx.lineWidth = Math.max(2, canvas.width / 400);
      for (const a of annotations) {
        ctx.strokeRect(a.rect[0], a.rect[1], a.rect[2], a.rect[3]);
      }
    }

    const encoded = await encodeShot(canvas);
    const comma = encoded.dataUrl.indexOf(',');
    if (comma < 0) return null;
    const mime = encoded.dataUrl.slice(5, encoded.dataUrl.indexOf(';'));
    return {
      mime: mime || 'image/webp',
      dataBase64: encoded.dataUrl.slice(comma + 1),
      width: encoded.width,
      height: encoded.height,
      bytes: encoded.bytes,
    };
  } catch {
    return null;
  }
}
